import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import {
  type ISignatureService,
  type SignatureResponse,
  SignatureService,
} from '../../services/signature';
import { useAnalytics } from '../analytics/use-analytics';
import { useValidatedTenantId } from '../auth/use-validated-tenant-id';
import { signatureQueryKeys } from './query-keys';

export function useSaveSignature() {
  const service = useInjection<ISignatureService>(SignatureService);
  const queryClient = useQueryClient();
  const { trackEvent } = useAnalytics();
  const currentTenantId = useValidatedTenantId();

  return useMutation({
    mutationFn: (dataUrl: string) => service.saveSignature(dataUrl),
    onSuccess: (response: SignatureResponse) => {
      if (currentTenantId) {
        queryClient.setQueryData(signatureQueryKeys.me(currentTenantId), response);
      }
      trackEvent('signature_save', {});
    },
    onError: (err) => {
      console.error('useSaveSignature error:', err);
    },
  });
}

/**
 * Confirms a replicated signature on the current site — the per-site act of
 * will that closes the "signed for another site" evidentiary gap (Fase 1
 * mitigation). Idempotent on the backend; safe to call even when the
 * signature is already native/confirmed.
 */
export function useConfirmSignature() {
  const service = useInjection<ISignatureService>(SignatureService);
  const queryClient = useQueryClient();
  const currentTenantId = useValidatedTenantId();

  return useMutation({
    mutationFn: () => service.confirmSignature(),
    onSuccess: (response: SignatureResponse) => {
      if (currentTenantId) {
        queryClient.setQueryData(signatureQueryKeys.me(currentTenantId), response);
      }
    },
    onError: (err) => {
      console.error('useConfirmSignature error:', err);
    },
  });
}

export function useDeleteSignature() {
  const service = useInjection<ISignatureService>(SignatureService);
  const queryClient = useQueryClient();
  const currentTenantId = useValidatedTenantId();

  return useMutation({
    mutationFn: () => service.deleteSignature(),
    onSuccess: () => {
      if (currentTenantId) {
        queryClient.setQueryData<SignatureResponse>(signatureQueryKeys.me(currentTenantId), {
          signatureUrl: null,
          hasSignature: false,
          needsConfirmation: false,
        });
      }
    },
    onError: (err) => {
      console.error('useDeleteSignature error:', err);
    },
  });
}
